/**
 * MultiLocationInput
 *
 * True multi-select Origin/Destination picker. Mirrors main's
 * LocationAutocomplete behavior 1:1:
 *   - Cities are EXCLUSIVE — picking a city replaces the entire selection.
 *   - States and zones STACK — added on top of existing non-city items.
 *   - Backspace on empty input removes the last chip.
 *   - Per-chip × button removes that chip.
 *
 * Selected items render as inline chips inside the input box. The search field
 * sits to their right and shares the same border. The whole component is a
 * controlled wrapper: parent provides the LocationData[] value and an
 * onChange(next).
 *
 * Underlying widget is the shared LocationSearch (includeStates + includeZones).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { LocationSearch } from 'components/LocationSearch/LocationSearch'
import { Chip } from 'components/ds'
import type { StateLocationData, ZoneLocationData } from 'utils/location-search/LocationDataSearchV2'
import type { LocationData } from '../../types/filters'
import {
    addBulk,
    addState,
    addZone,
    isCityLocation,
    isZoneLocation,
    locationChipId,
    locationChipLabel,
    removeLocation,
    selectCity,
} from '../../utils/locationSelection'
import { cn } from 'utils/cn'

interface MultiLocationInputProps {
    values: LocationData[]
    onChange: (next: LocationData[]) => void
    placeholder?: string
    disabled?: boolean
    isConnected?: boolean
    isFirstInGroup?: boolean
    isLastInGroup?: boolean
    error?: boolean
    /** Offer DAT zone suggestions (grouped states). Default true (Load Hub); callers that want plain
     *  state-only multi-select (e.g. the box-truck board) pass false. */
    includeZones?: boolean
    /** Render the suggestion listbox inline (disablePortal) — required inside MUI Dialog focus
     *  traps and the Telegram webview (TWA), where portals misbehave. */
    insideDialog?: boolean
}

/** Selection semantics (city exclusive, states/zones stack) live in one place — see utils/locationSelection. */

export const MultiLocationInput = ({
    values,
    onChange,
    placeholder = 'City, state or zone',
    disabled = false,
    isConnected = false,
    isFirstInGroup = false,
    isLastInGroup = false,
    error = false,
    includeZones = true,
    insideDialog = false,
}: MultiLocationInputProps) => {
    /** Selection rule (matches main):
     *   - Pick a city → replaces the entire selection with that single city.
     *   - Pick a state/zone → if existing has a city, replace; otherwise stack. */
    // Incrementing reset key — bumped after every successful selection so the
    // LocationSearch widget clears its typed query, regardless of whether the
    // chip count changed (picking city-after-city keeps values.length=1).
    //
    // Remounting blurs the embedded MUI input, so we capture an outer wrapper ref
    // and refocus the inner <input> after every reset. Without this the dispatcher
    // has to click back into the field after every city/state/zone selection.
    const wrapperRef = useRef<HTMLDivElement | null>(null)
    const [resetTick, setResetTick] = useState(0)
    const pendingFocusRef = useRef(false)
    const bumpReset = useCallback(() => {
        pendingFocusRef.current = true
        setResetTick((n) => n + 1)
    }, [])

    useEffect(() => {
        if (!pendingFocusRef.current) return
        pendingFocusRef.current = false
        // Defer one frame so the remounted LocationSearch + MUI Autocomplete have
        // attached their input element before we focus it.
        const id = requestAnimationFrame(() => {
            const input = wrapperRef.current?.querySelector<HTMLInputElement>('input')
            input?.focus()
        })
        return () => cancelAnimationFrame(id)
    }, [resetTick])

    const handleCitySelect = useCallback(
        (loc: { city: string; stateCode: string; latitude: number; longitude: number }) => {
            onChange(selectCity(loc))
            bumpReset()
        },
        [onChange, bumpReset]
    )

    const handleStateSelect = useCallback(
        (state: StateLocationData) => {
            onChange(addState(values, state.stateCode))
            bumpReset()
        },
        [values, onChange, bumpReset]
    )

    const handleZoneSelect = useCallback(
        (zone: ZoneLocationData) => {
            onChange(addZone(values, { zoneCode: zone.zoneCode, states: zone.states }))
            bumpReset()
        },
        [values, onChange, bumpReset]
    )

    // Bulk paste — `"TX,CA,FL"` or `"TXCAFL"` is parsed by the LocationSearch widget into a list of
    // states/zones; they stack, deduped, replacing any existing city selection.
    const handleBulkInput = useCallback(
        (items: (StateLocationData | ZoneLocationData)[]) => {
            onChange(addBulk(values, items))
            bumpReset()
        },
        [values, onChange, bumpReset]
    )

    const handleRemove = useCallback((id: string) => onChange(removeLocation(values, id)), [values, onChange])

    const handleRemoveLast = useCallback(() => {
        if (values.length === 0) return
        onChange(values.slice(0, -1))
    }, [values, onChange])

    // Hover-popover state for the overflow +N chip. We track a tiny open flag
    // and the anchor rect to position the popover. Keeps render simple; no
    // portal needed for an in-flow tooltip-style list.
    const [overflowOpen, setOverflowOpen] = useState(false)
    const overflowRef = useRef<HTMLDivElement | null>(null)
    const VISIBLE_CAP = 4

    const chips = useMemo(
        () =>
            values.map((v) => ({
                id: locationChipId(v),
                label: locationChipLabel(v),
                type: isZoneLocation(v) ? 'zone' : isCityLocation(v) ? 'city' : 'state',
            })),
        [values]
    )

    return (
        <div
            ref={wrapperRef}
            className={cn(
                'flex items-center h-8 text-xs font-[Inter,sans-serif] transition-colors w-full min-w-0 flex-1 gap-1',
                'border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-1.5',
                'focus-within:border-blue-600 focus-within:ring-2 focus-within:ring-blue-600/20',
                isConnected && !isFirstInGroup && '-ml-px',
                isConnected && isFirstInGroup && 'rounded-r-none border-r-0 rounded-l-md',
                isConnected && isLastInGroup && 'rounded-l-none rounded-r-md',
                isConnected && !isFirstInGroup && !isLastInGroup && 'rounded-none border-r-0',
                !isConnected && 'rounded-md',
                error && '!border-red-500 focus-within:!ring-red-500/20',
                '[&_input]:font-[Inter,sans-serif] [&_input]:font-medium [&_input]:text-xs [&_input]:text-slate-900 dark:[&_input]:text-slate-100',
                '[&_input::placeholder]:font-[Inter,sans-serif] [&_input::placeholder]:font-medium [&_input::placeholder]:text-xs',
                '[&_input::placeholder]:opacity-100 [&_input::placeholder]:text-slate-400 dark:[&_input::placeholder]:text-slate-500'
            )}
        >
            {/* Chips — render all selected inline up to VISIBLE_CAP; remainder
                rolls into a single +N chip with a hover popover so the user can
                still see what's in there. Each type maps to a DS Chip color so
                cities / states / zones stay visually distinct. */}
            {chips.slice(0, VISIBLE_CAP).map((c) => (
                <Chip
                    key={c.id}
                    size="sm"
                    variant="soft"
                    color={c.type === 'city' ? 'primary' : c.type === 'zone' ? 'warning' : 'default'}
                    onRemove={() => handleRemove(c.id)}
                    className="max-w-[110px] shrink-0"
                >
                    <span className="truncate" title={c.label}>
                        {c.label}
                    </span>
                </Chip>
            ))}
            {chips.length > VISIBLE_CAP && (
                <div ref={overflowRef} className="relative shrink-0" onMouseEnter={() => setOverflowOpen(true)} onMouseLeave={() => setOverflowOpen(false)}>
                    <Chip size="sm" variant="soft" color="primary" className="shrink-0 cursor-default">
                        +{chips.length - VISIBLE_CAP}
                    </Chip>
                    {overflowOpen && (
                        <div className="absolute z-50 top-full left-0 mt-1 min-w-[140px] max-w-[220px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg p-1.5 flex flex-col gap-1">
                            {chips.slice(VISIBLE_CAP).map((c) => (
                                <Chip
                                    key={c.id}
                                    size="sm"
                                    variant="soft"
                                    color={c.type === 'city' ? 'primary' : c.type === 'zone' ? 'warning' : 'default'}
                                    onRemove={() => handleRemove(c.id)}
                                    className="max-w-full"
                                >
                                    <span className="truncate" title={c.label}>
                                        {c.label}
                                    </span>
                                </Chip>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Search input — fills the remainder. Keyed by resetTick so
                LocationSearch clears its typed query after every successful
                selection (the previous values.length key missed city-after-city). */}
            <div className="flex-1 min-w-[60px]">
                <LocationSearch
                    key={resetTick}
                    placeholder={values.length === 0 ? placeholder : ''}
                    isDisabled={disabled}
                    onLocationSelect={handleCitySelect}
                    onStateSelect={handleStateSelect}
                    onZoneSelect={handleZoneSelect}
                    onBulkInput={handleBulkInput}
                    onRemoveLast={handleRemoveLast}
                    includeStates
                    includeZones={includeZones}
                    insideDialog={insideDialog}
                />
            </div>
        </div>
    )
}
